import { useEffect, useState } from "react";
import { MixCard } from "@/components/mix/MixCard";
import { AlbumCard } from "@/components/cards/AlbumCard";
import { ArtistCard } from "@/components/cards/ArtistCard";
import { PlaylistCard } from "@/components/cards/PlaylistCard";
import { QuickPickCard } from "@/components/cards/QuickPickCard";
import { SectionShelf } from "@/components/shelf/SectionShelf";
import { Skeleton } from "@/components/ui/Skeleton";
import { musicService } from "@/services/musicService";
import { mixService } from "@/services/mixService";
import { useAuth } from "@/hooks/useAuth";
import { usePlayer } from "@/hooks/usePlayer";
import { sfx } from "@/lib/sfx";
import { addDjHistory, getDjHistory } from "@/lib/djHistory";
import { djVoiceEnabled, primeDjVoice, setDjVoiceEnabled } from "@/lib/djVoice";
import { Link } from "react-router-dom";
import type { HomeFeed, MixList, Track } from "@/types/types";

type Banger = {
  id: string; title: string; duration_ms: number; popularity: number;
  artist: { id: string; name: string };
  album: { id: string; title: string; image_url: string | null };
};

function bangerToTrack(b: Banger): Track {
  return {
    id: b.id, title: b.title, disc: 1, track_no: 0,
    duration_ms: b.duration_ms, explicit: false, codec: null, bitrate_kbps: null,
    artist: { id: b.artist.id, name: b.artist.name, cover_id: null, image_url: null },
    album: {
      id: b.album.id, title: b.album.title, year: null,
      cover_id: b.album.image_url, image_url: b.album.image_url,
      artist: { id: b.artist.id, name: b.artist.name, cover_id: null, image_url: null },
    },
  } as Track;
}

export function HomePage() {
  const [feed, setFeed] = useState<HomeFeed | null>(null);
  const [bangers, setBangers] = useState<Banger[] | null>(null);
  // MUST live above the early return below — otherwise the hook count differs
  // between the "loading" render and the "loaded" render, blowing React up.
  const [djStarting, setDjStarting] = useState(false);
  const [mixes, setMixes] = useState<MixList | null>(null);
  const [djVoice, setDjVoice] = useState(djVoiceEnabled);
  const player = usePlayer();
  const { user } = useAuth();

  // "Conçus pour toi": the first build of the day runs in the worker, so
  // poll while it's cooking (up to ~2 min) instead of showing nothing.
  useEffect(() => {
    let alive = true;
    let tries = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = () => {
      mixService.list()
        .then((m) => {
          if (!alive) return;
          setMixes(m);
          if (m.building && tries++ < 30) timer = setTimeout(tick, 4000);
        })
        .catch((e) => console.warn("mixes failed", e));
    };
    tick();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, []);

  useEffect(() => {
    musicService.home().then(setFeed).catch(console.error);
    musicService.bangersForMe(15, { exclude: getDjHistory() })
      .then((rows) => setBangers(rows || []))
      .catch((e) => { console.warn("bangers failed", e); setBangers([]); });
  }, []);

  if (!feed) return (
    <div className="shelf">
      {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} height={220} />)}
    </div>
  );

  const bangerTracks = (bangers || []).map(bangerToTrack);

  /** Full DJ launch — refetch a freshly-shuffled mix from the backend
   *  (bypasses the 30 min Redis cache via a nonce), then plays it as an
   *  infinite queue that keeps refilling in the background. */
  const startDJ = async () => {
    if (djStarting) return;
    // must run inside the click, before any await (iOS autoplay unlock)
    primeDjVoice();
    setDjStarting(true);
    sfx.pop();
    try {
      const rows = await musicService.bangersForMe(25, { fresh: true, exclude: getDjHistory() });
      setBangers(rows || []);
      const tracks = (rows || []).map(bangerToTrack);
      if (tracks.length) {
        addDjHistory(tracks.map((t) => t.id));
        await player.playDJMix(tracks);
      }
    } catch (e) {
      console.warn("dj launch failed", e);
      if (bangerTracks.length) await player.playDJMix(bangerTracks);
    } finally { setDjStarting(false); }
  };

  /** Single-card play — just plays that one track, no auto-refill. */
  const playOne = (startIdx: number) => {
    if (!bangerTracks.length) return;
    sfx.pop();
    void player.play(bangerTracks, startIdx);
  };

  return (
    <div>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
        <h1 style={{ margin: 0 }}>Bonjour</h1>
        <Link to="/wrapped" className="wrapped-pill" data-sfx="tap">
          ✦ Ton bilan de {new Date().toLocaleDateString("fr-FR", { month: "long" })}
        </Link>
      </div>

      {bangers && bangers.length > 0 && (
        <section style={{ marginBottom: 28 }}>
          <div style={{
            background: "linear-gradient(135deg,#3b1d5c 0%,#1a0a2e 100%)",
            border: "1px solid rgba(167,139,255,.25)",
            borderRadius: 14, padding: "18px 20px",
            display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
            boxShadow: "0 8px 24px rgba(0,0,0,.35)",
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: 14,
              background: "linear-gradient(135deg,var(--accent),#5b21b6)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 26, fontWeight: 900, color: "#000",
              boxShadow: "0 6px 18px rgba(167,139,255,.4)",
            }}>DJ</div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: 0.4 }}>Ton DJ</div>
              <div style={{ fontSize: 13, opacity: .75, marginTop: 2 }}>
                {djVoice ? "Il te parle entre les sons, comme à la radio" : "Mode silencieux : que de la musique"}
              </div>
            </div>
            <button className={"dj-voice-toggle" + (djVoice ? " on" : "")}
                    title={djVoice ? "Couper la voix du DJ" : "Activer la voix du DJ"}
                    onClick={() => { sfx.tap(); setDjVoiceEnabled(!djVoice); setDjVoice(!djVoice); }}>
              {djVoice ? "🎙 Voix activée" : "🔇 Voix coupée"}
            </button>
            <button className="btn primary" onClick={startDJ} disabled={djStarting}
                    style={{ padding: "10px 22px", fontSize: 15 }}>
              {djStarting ? "…" : "▶ Lancer"}
            </button>
          </div>
          <div className="shelf" style={{ marginTop: 14 }}>
            {bangers.slice(0, 12).map((b, i) => (
              <QuickPickCard
                key={b.id}
                track={bangerTracks[i]}
                onPlay={() => playOne(i)}
              />
            ))}
          </div>
        </section>
      )}

      {mixes && (mixes.mixes.length > 0 || mixes.building) && (
        <SectionShelf title={`Conçus pour ${user?.display_name || user?.username || "toi"}`}>
          {mixes.mixes.length > 0
            ? mixes.mixes.map((m) => <MixCard key={m.id} mix={m} />)
            : Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="card">
                  <Skeleton height={150} radius={6} />
                  <div style={{ marginTop: 10 }}><Skeleton width="80%" /></div>
                </div>
              ))}
        </SectionShelf>
      )}

      {feed.quick_picks.length > 0 && (
        <SectionShelf title="Sélectionné pour toi">
          {feed.quick_picks.map((t) => <QuickPickCard key={t.id} track={t} />)}
        </SectionShelf>
      )}

      {feed.recently_played.length > 0 && (
        <SectionShelf title="Écoutés récemment">
          {feed.recently_played.map((a) => <AlbumCard key={a.id} album={a} />)}
        </SectionShelf>
      )}

      {feed.new_releases.length > 0 && (
        <SectionShelf title="Nouveautés">
          {feed.new_releases.map((a) => <AlbumCard key={a.id} album={a} />)}
        </SectionShelf>
      )}

      {feed.suggested_artists.length > 0 && (
        <SectionShelf title="Artistes suggérés">
          {feed.suggested_artists.map((a) => <ArtistCard key={a.id} artist={a} />)}
        </SectionShelf>
      )}

      {feed.made_for_you.length > 0 && (
        <SectionShelf title="Tes playlists">
          {feed.made_for_you.map((p) => <PlaylistCard key={p.id} playlist={p} />)}
        </SectionShelf>
      )}
    </div>
  );
}
